/**
 * Recibos.
 *
 * La pantalla sirve para dos cosas: **guardar la prueba** de lo que se pagó y
 * **repartir la compra entre categorías** sin sumar a mano.
 *
 * La parte que de verdad ayuda es la cuenta que se ve mientras se escribe: la suma de
 * los artículos tiene que dar el total del recibo, y la pantalla lo comprueba sola. Si
 * no cuadra, lo dice antes de guardar. Es exactamente lo que se hace a mano con una
 * calculadora, y es donde más se equivoca uno.
 *
 * La lectura automática del texto de la foto es **opcional** y se avisa de lo que
 * cuesta antes de usarla.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Check, ImagePlus, Plus, ScanText, Trash2, TriangleAlert, X } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import { formatCOP, parseAmountFromText, todayISO, toLocalTimeHHmm } from '@eldescuadre/core';

import {
  confirmReceipt,
  deleteReceipt,
  getAccounts,
  getCategories,
  getReceipts,
  suggestReceiptItems,
  uploadReceipt,
} from '../lib/endpoints';
import { ApiError } from '../lib/api';
import { Card, EmptyState, ErrorNote, Field, Money, Note, SubmitButton } from '../components/ui';

/** Un artículo mientras se está escribiendo. */
interface DraftItem {
  id: string;
  description: string;
  amountText: string;
  categoryId: string;
  /**
   * De dónde salió la categoría:
   *
   *  · `MEMORY` — de lo que tú mismo enseñaste antes. Lo más fiable.
   *  · `GUESS`  — de la lista de palabras. Puede equivocarse.
   *  · `NONE`   — no se sabe. **Hay que preguntarte.**
   *
   * La pantalla lo necesita para no tratar igual lo que nos dijiste que lo que nos
   * estamos inventando. Si no, una suposición y un dato se verían idénticos.
   */
  source: 'MEMORY' | 'GUESS' | 'NONE';
}

const emptyItem = (): DraftItem => ({
  id: crypto.randomUUID(),
  description: '',
  amountText: '',
  categoryId: '',
  source: 'NONE',
});

export function ReceiptsPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const [photo, setPhoto] = useState<{ dataUrl: string; mimeType: string; name: string } | null>(null);
  const [merchant, setMerchant] = useState('');
  const [totalText, setTotalText] = useState('');
  const [items, setItems] = useState<DraftItem[]>([emptyItem()]);
  const [accountId, setAccountId] = useState('');
  const [defaultCategoryId, setDefaultCategoryId] = useState('');
  const [reading, setReading] = useState(false);
  const [readWarning, setReadWarning] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ id: string; total: number } | null>(null);

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => getAccounts() });
  const categories = useQuery({
    queryKey: ['categories', 'EXPENSE'],
    queryFn: () => getCategories('EXPENSE'),
  });
  const receipts = useQuery({ queryKey: ['receipts'], queryFn: getReceipts });

  const total = parseAmountFromText(totalText) ?? 0;

  /** La suma de lo escrito, que es lo que tiene que cuadrar con el total. */
  const itemsSum = useMemo(
    () =>
      items.reduce((acc, item) => acc + (parseAmountFromText(item.amountText) ?? 0), 0),
    [items],
  );

  const difference = total - itemsSum;
  const matches = total > 0 && Math.abs(difference) === 0;

  const chosenAccountId = accountId || accounts.data?.accounts[0]?.id || '';

  /** La categoría de un artículo, con la del recibo como respaldo. */
  const categoryFor = (item: DraftItem): string | undefined =>
    item.categoryId || defaultCategoryId || undefined;

  const problem = useMemo(() => {
    if (!photo) return 'Hazle una foto al recibo o elige una imagen.';
    if (total <= 0) return 'Escribe el total del recibo.';
    if (!chosenAccountId) return 'Elige de qué cuenta salió el dinero.';
    if (itemsSum > total) {
      return `Los artículos suman ${formatCOP(itemsSum)} y el total es ${formatCOP(total)}. Corrige antes de guardar.`;
    }
    return null;
  }, [photo, total, chosenAccountId, itemsSum]);

  const save = useMutation({
    mutationFn: async () => {
      if (!photo) throw new Error('sin foto');

      const kind = 'EXPENSE' as const;
      const categoryId = defaultCategoryId || undefined;
      const filled = items.filter((item) => item.description.trim() && (parseAmountFromText(item.amountText) ?? 0) > 0);

      // 1. Se sube la foto y se guarda el recibo con lo que se escribió.
      const uploaded = await uploadReceipt({
        contentBase64: photo.dataUrl,
        mimeType: photo.mimeType,
        fileName: photo.name,
        merchant: merchant.trim() || undefined,
        amount: total,
        items: filled.map((item) => ({
          description: item.description.trim(),
          amount: parseAmountFromText(item.amountText) ?? 0,
          categoryId: item.categoryId || null,
          // Al guardar, la categoría es la que el usuario dejó: ya no es una
          // suposición, es su decisión. Y es lo que se aprenderá.
          source: item.categoryId ? ('MEMORY' as const) : ('NONE' as const),
          confidence: 1,
        })),
        engine: 'manual',
        confidence: 1,
      });

      // 2. Se confirma y se crea el movimiento, con la clave de idempotencia para
      //    que un doble toque no cree dos gastos.
      const key = crypto.randomUUID();
      await confirmReceipt(
        uploaded.receipt.id,
        {
          kind,
          amount: total,
          date: todayISO(),
          time: toLocalTimeHHmm(new Date()),
          accountId: chosenAccountId,
          categoryId,
          merchant: merchant.trim() || undefined,
          items:
            filled.length > 0
              ? filled.map((item) => ({
                  description: item.description.trim(),
                  amount: parseAmountFromText(item.amountText) ?? 0,
                  categoryId: categoryFor(item) ?? null,
                }))
              : undefined,
        },
        key,
      );

      return { id: uploaded.receipt.id, total };
    },
    onSuccess: async (result) => {
      setSaved(result);
      reset();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['receipts'] }),
        queryClient.invalidateQueries({ queryKey: ['transactions'] }),
        queryClient.invalidateQueries({ queryKey: ['accounts'] }),
        queryClient.invalidateQueries({ queryKey: ['summary'] }),
      ]);
    },
  });

  const remove = useMutation({
    mutationFn: deleteReceipt,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['receipts'] });
    },
  });

  function reset(): void {
    setPhoto(null);
    setMerchant('');
    setTotalText('');
    setItems([emptyItem()]);
  }

  /** Lee la imagen elegida y la deja lista para subir. */
  const readFile = async (file: File): Promise<void> => {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error('No se pudo leer la imagen.'));
      reader.readAsDataURL(file);
    });

    setPhoto({ dataUrl, mimeType: file.type || 'image/jpeg', name: file.name });
    setSaved(null);
  };

  /**
   * Lee el texto de la foto **en el dispositivo**.
   *
   * Es opcional y se avisa antes: la primera vez hay que descargar el motor y los
   * datos del idioma. La foto no sale del teléfono para esto: el texto se extrae aquí
   * y solo el texto se envía al servidor para interpretarlo.
   */
  const readText = async (): Promise<void> => {
    if (!photo) return;
    setReading(true);

    try {
      const { createWorker } = await import('tesseract.js');

      const worker = await createWorker('spa', 1, {
        // El motor se sirve desde **nuestro propio servidor**, no desde un CDN: el
        // código que lee tus recibos no debería depender de un tercero.
        //
        // -----------------------------------------------------------------------
        //  Por qué la carpeta se llama «ocr» y no «tesseract»
        // -----------------------------------------------------------------------
        //
        // Estaba en `/tesseract/` y **no funcionaba en producción**: el servidor
        // devolvía el archivo perfectamente (HTTP 200, 123 KB), pero al pedirlo por la
        // dirección pública llegaba un **502** generado por Cloudflare.
        //
        // Se comprobó con el mismo archivo copiado a otras rutas: `/ocr/`, `/lib/` y
        // `/motor-ocr/` daban 200; `/tesseract/` daba 502. No era el tamaño —un archivo
        // de 587 KB desde `/assets/` sí pasa— ni el archivo, sino **la palabra**: es el
        // nombre que también usa el sistema antirrobots de Cloudflare, y su filtro la
        // bloquea.
        //
        // La solución es no usar esa palabra en la ruta. Si algún día se cambia, hay
        // que comprobar que la ruta nueva responde antes de darla por buena.
        workerPath: '/ocr/worker.min.js',
        corePath: '/ocr/',
        // Los datos del idioma español se descargan la primera vez, porque son
        // 10 MB que no tiene sentido meter en el paquete para quien nunca use esto.
        // Quedan en la caché del navegador y no se vuelven a pedir.
      });

      const result = await worker.recognize(photo.dataUrl);
      await worker.terminate();

      // Se envía **solo el texto**, nunca la imagen.
      const suggestion = await suggestReceiptItems(result.data.text);

      if (suggestion.merchant) setMerchant(suggestion.merchant);
      if (suggestion.amount) setTotalText(String(suggestion.amount));

      if (suggestion.items.length > 0) {
        setItems(
          suggestion.items.map((item) => ({
            id: crypto.randomUUID(),
            description: item.description,
            amountText: String(item.amount),
            categoryId: item.categoryId ?? '',
            // Se conserva de dónde vino, para poder decírselo al usuario y para
            // preguntarle solo lo que de verdad no se sabe.
            source: item.source ?? 'NONE',
          })),
        );
      }

      setReadWarning(suggestion.warning);
    } catch {
      setReadWarning(
        'No se pudo leer el texto de la foto. Puedes escribir los artículos a mano: ' +
          'la foto se guarda igual.',
      );
    } finally {
      setReading(false);
    }
  };

  const error = save.error instanceof ApiError ? save.error : null;

  return (
    <div className="space-y-4 animate-ed-fade-in">
      <header>
        <h1 className="font-display text-2xl font-extrabold tracking-tight">Recibos</h1>
        <p className="mt-0.5 text-sm text-ed-text-muted">
          Fotografía el recibo y reparte la compra entre categorías.
        </p>
      </header>

      {saved && (
        <div className="flex items-center gap-2 rounded-ed border border-ed-success/40 bg-ed-success/10 px-3 py-2 text-sm text-ed-success">
          <Check size={16} aria-hidden="true" />
          Guardado: <Money amount={saved.total} /> con su foto.
        </div>
      )}

      {/* Paso 1: la foto */}
      <Card title="1 · La foto">
        {!photo ? (
          <label className="flex cursor-pointer flex-col items-center gap-3 rounded-ed border-2 border-dashed border-ed-bg-border/50 px-4 py-8 text-center transition-colors hover:border-ed-gold/50">
            <Camera size={32} className="text-ed-text-muted" aria-hidden="true" />
            <span className="font-display font-bold">Hazle una foto al recibo</span>
            <span className="max-w-sm text-xs text-ed-text-muted">
              Se guarda junto al movimiento, para que puedas consultarla cuando quieras.
            </span>
            <span className="ed-btn-secondary !w-auto !px-4">
              <ImagePlus size={16} aria-hidden="true" />
              Elegir o hacer la foto
            </span>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              capture="environment"
              className="sr-only"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void readFile(file);
              }}
            />
          </label>
        ) : (
          <div className="space-y-3">
            <div className="relative overflow-hidden rounded-ed border border-ed-bg-border/40">
              <img src={photo.dataUrl} alt="Recibo" className="max-h-72 w-full object-contain" />
              <button
                type="button"
                className="absolute right-2 top-2 rounded-full bg-ed-bg-abyss/80 p-2 text-ed-text-silver"
                onClick={() => {
                  reset();
                  setReadWarning(null);
                }}
                aria-label="Quitar la foto"
              >
                <X size={16} />
              </button>
            </div>

            <button
              type="button"
              className="ed-btn-secondary w-full text-xs"
              disabled={reading}
              onClick={() => void readText()}
            >
              <ScanText size={15} aria-hidden="true" />
              {reading ? 'Leyendo el texto…' : 'Leer el texto de la foto'}
            </button>

            <p className="text-xs text-ed-text-muted">
              La lectura ocurre <strong>en tu teléfono</strong>: la foto no se envía a
              ningún sitio para interpretarla. La primera vez descarga el motor de lectura
              (unos 13 MB); después queda guardado.
            </p>

            {readWarning && (
              <p className="flex items-start gap-1.5 text-xs text-ed-gold">
                <TriangleAlert size={13} className="mt-0.5 shrink-0" aria-hidden="true" />
                {readWarning}
              </p>
            )}
          </div>
        )}
      </Card>

      {/* Paso 2: los datos */}
      {photo && (
        <>
          <Card title="2 · El total y los artículos">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Comercio" htmlFor="comercio-recibo">
                <input
                  id="comercio-recibo"
                  className="ed-input"
                  value={merchant}
                  onChange={(e) => setMerchant(e.target.value)}
                  placeholder="D1, Ara, Éxito…"
                  autoComplete="off"
                />
              </Field>

              <Field label="Total del recibo" htmlFor="total-recibo">
                <input
                  id="total-recibo"
                  className="ed-input ed-money"
                  inputMode="numeric"
                  value={totalText}
                  onChange={(e) => setTotalText(e.target.value)}
                  placeholder="45.000"
                  autoComplete="off"
                />
              </Field>
            </div>

            <ul className="mt-4 space-y-3">
              {items.map((item, index) => {
                /*
                 * La categoría de esta línea, y de dónde salió.
                 *
                 * Se enseña porque el usuario tiene derecho a saber si la aplicación
                 * **lo sabe por él** o **se lo está inventando**. Las dos cosas no
                 * merecen la misma confianza.
                 */
                const elegida = item.categoryId || '';

                return (
                  <li
                    key={item.id}
                    className={`rounded-ed border p-2 ${
                      elegida
                        ? 'border-ed-bg-border/40'
                        : // Sin categoría: se marca para que se vea que hay que decidir.
                          'border-ed-gold/50 bg-ed-gold/5'
                    }`}
                  >
                    <div className="flex gap-2">
                      <input
                        className="ed-input flex-1"
                        value={item.description}
                        onChange={(e) =>
                          setItems((current) =>
                            current.map((row) =>
                              row.id === item.id
                                ? // Si cambia el nombre, la categoría deja de ser segura:
                                  // era de otro producto.
                                  { ...row, description: e.target.value, source: 'NONE' }
                                : row,
                            ),
                          )
                        }
                        placeholder={`Artículo ${index + 1}`}
                        aria-label={`Descripción del artículo ${index + 1}`}
                        autoComplete="off"
                      />
                      <input
                        className="ed-input ed-money !w-28"
                        inputMode="numeric"
                        value={item.amountText}
                        onChange={(e) =>
                          setItems((current) =>
                            current.map((row) =>
                              row.id === item.id ? { ...row, amountText: e.target.value } : row,
                            ),
                          )
                        }
                        placeholder="0"
                        aria-label={`Importe del artículo ${index + 1}`}
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        className="ed-btn-ghost !w-auto !px-2 text-ed-text-muted"
                        onClick={() => setItems((current) => current.filter((row) => row.id !== item.id))}
                        disabled={items.length === 1}
                        aria-label={`Quitar el artículo ${index + 1}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>

                    {/* La categoría de esta línea, con lo que se sabe de ella. */}
                    <div className="mt-2 flex items-center gap-2">
                      <select
                        className="ed-input flex-1 !py-1 text-sm"
                        value={elegida}
                        aria-label={`Categoría del artículo ${index + 1}`}
                        onChange={(e) =>
                          setItems((current) =>
                            current.map((row) =>
                              row.id === item.id
                                ? {
                                    ...row,
                                    categoryId: e.target.value,
                                    // La eligió él: ya no es una suposición.
                                    source: 'MEMORY',
                                  }
                                : row,
                            ),
                          )
                        }
                      >
                        <option value="">¿En qué categoría va?</option>
                        {(categories.data?.categories ?? []).map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>

                      {elegida && item.source === 'MEMORY' && item.categoryId && (
                        <span
                          className="shrink-0 rounded-full bg-ed-success/15 px-2 py-0.5 text-[11px] text-ed-success"
                          title="Ya lo sabías tú: lo aprendí de una factura anterior"
                        >
                          lo sabía por ti
                        </span>
                      )}

                      {elegida && item.source === 'GUESS' && (
                        <span
                          className="shrink-0 rounded-full bg-ed-cyan/15 px-2 py-0.5 text-[11px] text-ed-cyan-light"
                          title="Lo he adivinado por el nombre. Si no es así, cámbialo y lo recordaré"
                        >
                          adivinado
                        </span>
                      )}

                      {!elegida && (
                        <span className="shrink-0 text-[11px] text-ed-gold">
                          falta elegirla
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* Cuántas quedan sin decidir: es lo que hay que atender antes de guardar. */}
            {items.some((item) => !item.categoryId && !defaultCategoryId) && (
              <p className="mt-2 text-xs text-ed-gold">
                Hay líneas sin categoría. Elige la de cada una, o ponle una a todo el recibo
                abajo. Lo que elijas lo recordaré para la próxima vez.
              </p>
            )}

            <button
              type="button"
              className="ed-btn-ghost mt-2 w-full text-xs"
              onClick={() => setItems((current) => [...current, emptyItem()])}
            >
              <Plus size={14} aria-hidden="true" />
              Añadir artículo
            </button>

            {/* La cuenta que se hace sola: es donde más se equivoca uno a mano. */}
            {total > 0 && (
              <div
                className={`mt-4 rounded-ed border p-3 text-sm ${
                  matches
                    ? 'border-ed-success/40 bg-ed-success/10'
                    : 'border-ed-gold/40 bg-ed-gold/10'
                }`}
              >
                <div className="flex justify-between">
                  <span className="text-ed-text-muted">Suma de los artículos</span>
                  <Money amount={itemsSum} className="font-semibold" />
                </div>
                <div className="mt-1 flex justify-between">
                  <span className="text-ed-text-muted">Total del recibo</span>
                  <Money amount={total} className="font-semibold" />
                </div>
                <div className="mt-2 border-t border-ed-bg-border/30 pt-2">
                  {matches ? (
                    <p className="flex items-center gap-1.5 text-ed-success">
                      <Check size={14} aria-hidden="true" />
                      Cuadra. Los artículos suman exactamente el total.
                    </p>
                  ) : difference > 0 ? (
                    <p className="text-ed-gold">
                      Faltan <Money amount={difference} /> por repartir.
                    </p>
                  ) : (
                    <p className="text-ed-coral-light">
                      Te pasaste por <Money amount={Math.abs(difference)} />.
                    </p>
                  )}
                </div>
              </div>
            )}
          </Card>

          <Card title="3 · De dónde salió y en qué categoría">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Cuenta" htmlFor="cuenta-recibo">
                <select
                  id="cuenta-recibo"
                  className="ed-input"
                  value={chosenAccountId}
                  onChange={(e) => setAccountId(e.target.value)}
                >
                  <option value="">Elige una cuenta…</option>
                  {(accounts.data?.accounts ?? []).map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} · {formatCOP(account.balance)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                label="Categoría"
                htmlFor="categoria-recibo"
                hint="Se le pone a todo el recibo; cada artículo puede tener la suya."
              >
                <select
                  id="categoria-recibo"
                  className="ed-input"
                  value={defaultCategoryId}
                  onChange={(e) => setDefaultCategoryId(e.target.value)}
                >
                  <option value="">Sin categoría</option>
                  {(categories.data?.categories ?? []).map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </Card>

          {problem && <ErrorNote message={problem} />}
          {error && <ErrorNote message={error.detailMessage} />}

          <SubmitButton
            type="button"
            pending={save.isPending}
            disabled={problem !== null}
            onClick={() => save.mutate()}
          >
            Guardar el recibo y el gasto
          </SubmitButton>
        </>
      )}

      {/* Los recibos ya guardados */}
      {receipts.data && receipts.data.receipts.length > 0 && (
        <Card
          title="Recibos guardados"
          action={
            receipts.data.usedBytes > 0 ? (
              <span className="text-xs text-ed-text-muted">
                {(receipts.data.usedBytes / 1024 / 1024).toFixed(1)} MB
              </span>
            ) : undefined
          }
        >
          <ul className="divide-y divide-ed-bg-border/25">
            {receipts.data.receipts.slice(0, 12).map((receipt) => (
              <li key={receipt.id} className="flex items-center gap-3 py-2.5">
                <img
                  src={receipt.imageUrl}
                  alt=""
                  className="h-12 w-12 shrink-0 rounded-ed border border-ed-bg-border/40 object-cover"
                  loading="lazy"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm">
                    {receipt.merchant ?? 'Recibo sin comercio'}
                  </span>
                  <span className="block text-xs text-ed-text-muted">
                    {receipt.confirmedAt ? 'Registrado' : 'Pendiente'}
                    {receipt.items.length > 0 && <> · {receipt.items.length} artículos</>}
                  </span>
                </span>
                {receipt.amount !== null && (
                  <Money amount={receipt.amount} className="shrink-0 text-sm font-semibold" />
                )}
                <button
                  type="button"
                  className="shrink-0 text-ed-text-muted hover:text-ed-coral-light"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm('¿Borrar este recibo y su foto? El movimiento se conserva.')) {
                      remove.mutate(receipt.id);
                    }
                  }}
                  aria-label="Borrar el recibo"
                >
                  <Trash2 size={15} />
                </button>
              </li>
            ))}
          </ul>

          {receipts.data.pending > 0 && (
            <p className="mt-3 text-xs text-ed-gold">
              Tienes {receipts.data.pending}{' '}
              {receipts.data.pending === 1 ? 'recibo sin registrar' : 'recibos sin registrar'}.
            </p>
          )}
        </Card>
      )}

      {receipts.data && receipts.data.receipts.length === 0 && !photo && (
        <Card>
          <EmptyState
            title="Todavía no has guardado recibos"
            message="Cuando le hagas una foto a uno, quedará aquí con sus artículos. Sirve para repartir la compra entre categorías y para tener la prueba de lo que pagaste."
          />
        </Card>
      )}

      <Note>
        La foto se guarda <strong>en tu servidor</strong>, en una carpeta privada, y solo
        la puedes ver tú. La lectura del texto, cuando la usas, ocurre{' '}
        <strong>en tu teléfono</strong>: lo que se envía al servidor es el texto, nunca la
        imagen.
      </Note>
    </div>
  );
}
